import { useState } from "react"
import { t } from "translations"

import Accordion from "@mui/material/Accordion"
import AccordionDetails from "@mui/material/AccordionDetails"
import AccordionSummary from "@mui/material/AccordionSummary"
import Button from "@mui/material/Button"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/material/styles"

import { rewrites } from "lib/routes"

import Icon from "Components/Icon/Icon"

export interface IToolTemplateInstruction {
    icon: string
    instructionText: string
}

export interface IToolTemplateAccordion {
    title: string
    text: string
}

export default function ToolTemplatePage({
    toolItem,
    instructionTitle,
    instructions,
    accordions,
    cloudText
}: {
    toolItem: JSX.Element
    instructionTitle: string
    instructions: IToolTemplateInstruction[]
    accordions: IToolTemplateAccordion[]
    cloudText?: string
}) {
    return (
        <>
            <StyledMain>
                <StyledContentWrapper>
                    <div>{toolItem}</div>

                    <StyledAside>
                        <StyledAsideTitle
                            variant="h2"
                            component="h1">
                            {instructionTitle}
                        </StyledAsideTitle>

                        {instructions.map(instruction => (
                            <InstructionListItem
                                key={instruction.instructionText}
                                icon={instruction.icon}
                                instructionText={instruction.instructionText}
                            />
                        ))}

                        {accordions.map(accordion => (
                            <AsideAccordion
                                key={accordion.title}
                                title={accordion.title}
                                content={accordion.text}
                            />
                        ))}

                        {cloudText && <StyledCloud text={cloudText} />}
                    </StyledAside>
                </StyledContentWrapper>
            </StyledMain>
            <StyledBottomBanner>
                <StyledIcon
                    name="pregnancyOracle"
                    viewBox="0 0 212 113"
                />

                <StyledBottomTextWrapper>
                    <StyledBottomText variant="body2">
                        {t.pregnancyOracle.frontpageText}
                    </StyledBottomText>
                    <StyledButton
                        variant="outlined"
                        href={rewrites["/pregnancyoracle"] + "/foster/0"}
                        endIcon={<Icon name="arrowForward" />}>
                        {t.pregnancyOracle.frontpageButton}
                    </StyledButton>
                </StyledBottomTextWrapper>
            </StyledBottomBanner>
        </>
    )
}

const StyledMain = styled("main")(({ theme }) => ({
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(4),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    [theme.breakpoints.up("md")]: {
        padding: theme.spacing(4),
        marginTop: theme.spacing(6)
    }
}))

const StyledContentWrapper = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    backgroundColor: theme.palette.background.paper,
    gap: theme.spacing(2),
    [theme.breakpoints.up("md")]: {
        display: "grid",
        gap: theme.spacing(4),
        gridTemplateColumns: "1fr 1.5fr"
    }
}))

const StyledAside = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2)
}))

const StyledAsideTitle = styled(Typography)<{ component: string }>(
    ({ theme }) => ({
        textAlign: "center",
        marginTop: theme.spacing(2),
        [theme.breakpoints.up("md")]: {
            marginTop: 0,
            marginBottom: theme.spacing(2)
        }
    })
)

const StyledBottomBanner = styled("div")(({ theme }) => ({
    padding: theme.spacing(4),
    display: "flex",
    flexDirection: "column",
    backgroundColor: theme.palette.pregnancy.main,
    gap: theme.spacing(2),
    [theme.breakpoints.up("md")]: {
        display: "grid",
        gap: theme.spacing(4),
        gridTemplateColumns: "1fr 1.5fr"
    }
}))

const StyledIcon = styled(Icon)({
    fontSize: "14rem",
    margin: "auto",
    height: "auto"
})

const StyledBottomTextWrapper = styled("div")(({ theme }) => ({
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    marginBottom: 0,
    marginTop: "auto"
}))

const StyledBottomText = styled(Typography)(({ theme }) => ({
    margin: "0 auto",
    [theme.breakpoints.up("sm")]: {
        maxWidth: "80%"
    }
}))

const StyledButton = styled(Button)(({ theme }) => ({
    margin: "0 auto",
    width: "fit-content",
    height: "fit-content",
    borderColor: theme.palette.pregnancy.dark,
    backgroundColor: theme.palette.background.paper,
    "&:hover": {
        backgroundColor: theme.palette.pregnancy.light,
        borderColor: theme.palette.pregnancy.dark
    },
    "&:focus": {
        backgroundColor: theme.palette.pregnancy.dark,
        borderColor: theme.palette.pregnancy.dark,
        outline: "none"
    }
}))

function InstructionListItem({
    icon,
    instructionText
}: {
    icon: string
    instructionText: string
}) {
    return (
        <StyledInstructionItem key={instructionText}>
            <Icon name={icon} />
            <Typography variant="body1">{instructionText}</Typography>
        </StyledInstructionItem>
    )
}

const StyledInstructionItem = styled("div")(({ theme }) => ({
    gap: theme.spacing(1),
    display: "flex"
}))

function AsideAccordion({
    title,
    content
}: {
    title: string
    content: string
}) {
    const [open, setOpen] = useState(false)
    const toggleOpen = () => setOpen(!open)
    return (
        <StyledAccordion
            expanded={open}
            key={title}>
            <StyledAccordionSummary
                onClick={toggleOpen}
                expandIcon={<Icon name="expandMore" />}>
                <Typography
                    component="span"
                    variant="h5">
                    {title}
                </Typography>
            </StyledAccordionSummary>
            <StyledAccordionDetails>
                <Typography variant="body2">{content}</Typography>
            </StyledAccordionDetails>
        </StyledAccordion>
    )
}

const StyledAccordion = styled(Accordion)(({ theme }) => ({
    wordBreak: "break-word",
    backgroundColor: theme.palette.pregnancy.light,
    "&.MuiPaper-root": {
        margin: 0,
        marginTop: theme.spacing(1)
    }
}))

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
    backgroundColor: theme.palette.pregnancy.main
}))

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
    padding: theme.spacing(0, 2, 2, 2)
}))

function StyledCloud({ text }: { text: string }) {
    if (!text) return null
    return <StyledCloudText variant="caption">{text}</StyledCloudText>
}

const StyledCloudText = styled(Typography)(({ theme }) => ({
    color: theme.palette.common.black,
    textAlign: "center",
    fontFamily: "IBMPlexSerifExtraLightItalic",
    fontSize: "1.375rem",
    margin: "auto",
    padding: theme.spacing(1),
    [theme.breakpoints.up("md")]: {
        maxWidth: "70%"
    }
}))
